import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import styled, { css } from "styled-components";

import { isNonEmptyString, isRenderableNode } from "../../utils/reactNode";
import { useDevWarnOnce } from "../../utils/useDevWarnOnce";
import {
  inlineElementActionTypography,
  inlineElementHeight,
  inlineElementIconSize,
  controlPadX,
  controlPadY,
  controlRadius,
} from "../foundations/controlStyles";
import { interactiveSurfaceCss } from "../foundations/interactiveSurface";
import { isDataVizToneName, type DataVizToneName } from "../../theme/dataVizPalette";
import { buildIconButtonSurface } from "./iconButtonSurface";

export type IconButtonVariant = "ghost" | "surface" | "solid";
export type IconButtonTone =
  | "primary"
  | "secondary"
  | "neutral"
  | "warning"
  | "danger"
  | DataVizToneName;
export type IconButtonSize = "xs" | "sm" | "md" | "lg";
// LabelChip 의 iconPosition 과 같은 vocabulary("left" | "right") — 같은 패키지에 두 어휘를 두지 않는다.
export type IconButtonIconPosition = "left" | "right";

// 기존 public vocab(xs/sm/md/lg)과 geometry(24/28/32/40px)를 그대로 공용 절대 높이 foundation에
// 바인딩한다. sm/md는 같은 key의 입력·드롭다운·스위처와 한 줄 높이를 맞추고, xs는 clear 버튼처럼
// 컨트롤 안에 얹히는 affordance tier다. padY는 높이를 만들지 않는다: 높이는 min-height가 잡고
// 글리프는 중앙 정렬되므로,
// 세로 패딩은 아이콘+테두리 합이 min-height를 넘지 않게 낮게 둔다. icon-only는 이 padY를 가로에도 대칭
// 적용해 min-width/min-height 둘 다 바인딩 → 정확히 min 정사각(24/28/32/40). 텍스트 버튼만 가로에 padX로
// 라벨 여백을 준다. (기존엔 단일 padding이 커서 min을 넘겨 md가 34×34로 떴다.) 텍스트 버튼의 padX 는
// controlPadX 를 따른다 — 모든 size 에서 상하 < 좌우로, 라벨 앞뒤 여백이 같은 줄의 트리거·칩과 맞는다.
// padY 값 자체도 공용 controlPadY 소싱이다(값 불변 리팩터 — 정사각 기하의 단일 소스).
const SIZE = {
  xs: {
    min: inlineElementHeight.xs,
    padY: controlPadY.xs,
    padX: controlPadX.xs,
    gap: "0.28rem",
  },
  sm: {
    min: inlineElementHeight.sm,
    padY: controlPadY.sm,
    padX: controlPadX.sm,
    gap: "0.32rem",
  },
  md: {
    min: inlineElementHeight.md,
    padY: controlPadY.md,
    padX: controlPadX.md,
    gap: "0.4rem",
  },
  lg: {
    min: inlineElementHeight.lg,
    padY: controlPadY.lg,
    padX: controlPadX.lg,
    gap: "0.5rem",
  },
} as const;

const dataVizReducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StyledIconButton = styled.button<{
  $variant: IconButtonVariant;
  $tone: IconButtonTone;
  $size: IconButtonSize;
  $border: boolean;
  $selected: boolean;
  $hasText: boolean;
  $iconPosition: IconButtonIconPosition;
}>`
  box-sizing: border-box;
  display: inline-flex;
  width: fit-content;
  /* iconPosition="right" 는 DOM 순서(아이콘→라벨)를 두고 시각 순서만 뒤집는다(LabelChip 과 동일
     기법) — 접근성 이름·포커스 순서 불변, gap 은 row-reverse 에서도 그대로다. padX 가 좌우
     대칭이라 어느 쪽이든 라벨 앞뒤 여백은 같다. */
  flex-direction: ${({ $iconPosition }) => ($iconPosition === "right" ? "row-reverse" : "row")};
  align-items: center;
  justify-content: center;
  min-width: ${({ $size }) => SIZE[$size].min};
  min-height: ${({ $size }) => SIZE[$size].min};
  gap: ${({ $size, $hasText }) => ($hasText ? SIZE[$size].gap : "0")};
  padding: ${({ $size, $hasText }) =>
    $hasText ? `${SIZE[$size].padY} ${SIZE[$size].padX}` : SIZE[$size].padY};
  ${controlRadius}
  border: 1px solid;
  font: inherit;
  /* 자식 글리프(1em svg)의 em-basis. primary icon 공용 scale(14/14/16/20)을 쓰고, text 라벨
     <span>은 아래에서 자체 metric을 재지정하므로 이 값의 영향을 받지 않는다. */
  font-size: ${({ $size }) => inlineElementIconSize[$size]};
  line-height: 1;

  /* hover/active/focus/disabled 진행·transition·focus ring 은 공유 생성기가 소유 — 색 값만 IconButton
     팔레트(테마 tone × variant × selected)에서 resolve 해 넘긴다. */
  ${({ $variant, $tone, $selected, $border, theme }) =>
    interactiveSurfaceCss(buildIconButtonSurface(theme.color, $variant, $tone, $selected, $border))}

  ${({ $variant, $tone, $selected, $border, theme }) =>
    isDataVizToneName($tone) &&
    css`
      ${dataVizReducedMotion}
      :root[data-c6-theme="dark"] & {
        ${interactiveSurfaceCss(
          buildIconButtonSurface(theme.color, $variant, $tone, $selected, $border, "dark"),
        )}
        ${dataVizReducedMotion}
      }
    `}

  & > svg,
  & > i {
    flex-shrink: 0;
  }

  /* 라벨은 콘텐츠/값 텍스트와 다른 action-label role을 쓴다. action weight가 이미 강조를
     담당하므로 xs/sm/md caption, lg body + tight인 기존 배포 metric을 유지한다. */
  & > span {
    min-width: 0;
    font-size: ${({ $size }) => inlineElementActionTypography[$size].fontSize};
    font-weight: ${({ theme }) => theme.typography.weight.action};
    line-height: ${({ $size }) => inlineElementActionTypography[$size].lineHeight};
  }
`;

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  children?: ReactNode;
  // TBD FIXME(Gigone): IconButton을 Button으로 변경할지 고민 (아이콘을 안쓰는 경우도 있으므로)
  text?: ReactNode;
  variant?: IconButtonVariant;
  /** Action intent, or `dataViz.<hue>` for category/segment identity. Button states own the mixes. */
  tone?: IconButtonTone;
  size?: IconButtonSize;
  /**
   * Border override. When omitted, `surface` is outlined while `ghost` and `solid` are borderless.
   * While enabled, `selected=true` keeps a tone outline unless this is explicitly `false`.
   * For persistent solid toggles, change the icon and accessible name; use `surface` when the same
   * icon must communicate both toggle states.
   * Disabled styling removes the interaction tone. Solid retains a neutral filled shape, while
   * selected disabled buttons retain their value with neutral fill/outline cues.
   */
  border?: boolean;
  /** Toggle state. Providing it emits aria-pressed; omit it for plain action buttons. */
  selected?: boolean;
  /**
   * 텍스트 버튼에서 아이콘을 라벨 뒤(오른쪽)에 둔다. DOM 순서는 icon→label 그대로라(row-reverse)
   * 접근성 이름·포커스에 영향이 없다. icon-only 에선 자식이 하나라 무해한 no-op.
   */
  iconPosition?: IconButtonIconPosition;
}

/**
 * Compact icon/action button.
 *
 * Icon-only buttons need an accessible name; `selected` emits aria-pressed.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      children,
      text,
      variant = "ghost",
      tone = "primary",
      size = "md",
      border,
      selected,
      iconPosition = "left",
      disabled = false,
      type = "button",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-pressed": ariaPressed,
      title,
      ...rest
    },
    ref,
  ) => {
    const hasText = isRenderableNode(text);
    const hasAccessibleName =
      hasText ||
      isNonEmptyString(ariaLabel) ||
      isNonEmptyString(ariaLabelledBy) ||
      isNonEmptyString(title);
    // enabled selected는 aria-pressed 상태를 색 외의 outline으로도 구분해야 한다. 명시적 border
    // override가 있으면 호출자 의도를 우선한다. disabled에서는 neutral fill/outline이 선택 값을
    // 보존하므로 selected의 interaction tone은 남기지 않는다.
    const resolvedBorder = border ?? (variant === "surface" || selected === true);
    useDevWarnOnce(
      !hasAccessibleName,
      "[c6] Icon-only <IconButton> requires a non-empty accessible name. Provide `text`, `aria-label`, `aria-labelledby`, or `title`.",
    );

    return (
      <StyledIconButton
        ref={ref}
        type={type}
        $variant={variant}
        $tone={tone}
        $size={size}
        $border={resolvedBorder}
        $selected={selected ?? false}
        $hasText={hasText}
        $iconPosition={iconPosition}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-pressed={selected ?? ariaPressed}
        title={title}
        {...rest}
      >
        {children}
        {hasText ? <span>{text}</span> : null}
      </StyledIconButton>
    );
  },
);

IconButton.displayName = "IconButton";
